// useIncidents — the PUSH half of the recovery-notice surface (Brief 5 / pt10 +
// pt20). The core UX: a caught error is "caught → bounded → reversed,
// never cosmetic" — so a recovery must be ANNOUNCED, not handled silently. The
// existing SessionStatusPanel is the PULL view (you open it to catch up); this
// hook drives the transient stack that PROACTIVELY surfaces a caught-and-handled
// incident the instant it happens.
//
// It remembers the PREVIOUS snapshot of the recovery-relevant signals (the live
// stream health + the queued sends) and diffs it against the next via the pure
// `deriveIncidents` in @waytide/core (FACTS ONLY — no predictions). Each produced
// incident is accumulated into an active stack with:
//   - `recovered` notices auto-dismissed after a short delay (calm: the system is
//     fine again, the user doesn't need to act, so it clears itself);
//   - `needs_input` notices KEPT until the user acknowledges them (the one case
//     that genuinely needs them — never auto-cleared out from under them).
//
// PLATFORM BOUNDARY: this is shared UI, so it owns no Tauri + no app
// import. The signals are passed IN (the stream health + the outbox entries the
// surface already holds), so the hook is platform-neutral and unit-testable
// against a fake clock with no live backend.

import { useEffect, useMemo, useRef, useState } from "react";
import type {
  IncidentDescriptor,
  IncidentSnapshot,
  StreamHealth,
} from "@waytide/core";
import { deriveIncidents } from "@waytide/core";

/** How long a CALM (`recovered`) notice lingers before it auto-dismisses, ms.
 * Long enough to read one plain sentence, short enough not to nag — it is a
 * "by the way, that was handled" cue, not a task. `needs_input` notices ignore
 * this entirely (they stay until acknowledged). */
export const RECOVERED_TTL_MS = 6000;

/** The minimal projection of a queued send this hook needs — the same shape the
 * outbox exposes (key + attempts + a parked flag), kept structural so the hook
 * doesn't import the @waytide/ui store type and a caller can pass either the real
 * `OutboxEntry[]` (a superset, structurally assignable) or a hand-built list in a
 * test. */
export interface IncidentOutboxItem {
  key: string;
  attempts: number;
  /** Parked = the auto-retry budget is spent (the store's `failed` flag). */
  parked: boolean;
}

/** One live notice in the stack: the classified incident plus a stable id (for
 * the React key + acknowledge) and when it was raised (for the auto-dismiss
 * timer + a stable display order). */
export interface ActiveIncident {
  /** Stable id: the incident kind + the raise time — unique per raised notice,
   * and idempotent if the SAME kind is re-derived while still showing (we replace
   * rather than stack a duplicate, so a flapping connection shows ONE notice). */
  id: string;
  descriptor: IncidentDescriptor;
  /** Epoch-ms when this notice was raised (drives the auto-dismiss + ordering). */
  raisedAt: number;
}

export interface UseIncidents {
  /** The active notices, oldest-first (render as a top-of-thread stack). */
  notices: ActiveIncident[];
  /** Dismiss one notice by id — the affordance on a `needs_input` notice (and an
   * optional early-dismiss on a calm one). Idempotent. */
  dismiss: (id: string) => void;
}

/** The signals the hook diffs — the live connection health + the queued sends.
 * A surface already holds both (the channel hook's `health`, the outbox's
 * `pending`); it just forwards them here. */
export interface IncidentSignals {
  health: StreamHealth;
  outbox: IncidentOutboxItem[];
  /** Injectable clock (ms) for deterministic tests; defaults to the wall clock. */
  now?: number;
}

/**
 * Watch the recovery-relevant signals and surface a transient, honest notice for
 * each caught-and-handled (or needs-you) transition.
 *
 * Accumulation rules:
 *   - On every signal change, diff the prev snapshot → produce 0+ incidents.
 *   - A produced incident whose `kind` is already showing REPLACES the existing
 *     one (same id basis) rather than stacking a duplicate — a flapping drop/
 *     reconnect can't pile up identical notices.
 *   - `recovered` notices auto-dismiss `RECOVERED_TTL_MS` after they're raised.
 *   - `needs_input` notices persist until `dismiss(id)` (acknowledged).
 *
 * The auto-dismiss is driven by a single timer recomputed when the stack changes
 * — it fires for the soonest-expiring calm notice, drops the expired ones, and
 * reschedules. `needs_input` notices have no expiry, so they're never swept.
 */
export function useIncidents(signals: IncidentSignals): UseIncidents {
  const { health, outbox } = signals;

  // The previous snapshot, kept in a ref so the diff effect doesn't re-run on its
  // own state writes — only when the input signals actually change.
  const prevSnapshot = useRef<IncidentSnapshot | null>(null);
  const [notices, setNotices] = useState<ActiveIncident[]>([]);

  // Build the current snapshot from the passed signals. Memoised on the concrete
  // values (health + each send's key/attempts/parked) so a re-render with the
  // SAME signals doesn't re-diff (which would otherwise re-raise nothing, but we
  // avoid the churn). The outbox is mapped to the pure core shape.
  const snapshot = useMemo<IncidentSnapshot>(
    () => ({
      connection: { health },
      outbox: outbox.map((e) => ({
        key: e.key,
        attempts: e.attempts,
        parked: e.parked,
      })),
    }),
    [health, outbox],
  );

  // A stable now for this diff pass (injectable for tests). Read from a ref so
  // the effect's dependency list stays the snapshot only — the clock is incidental.
  const nowRef = useRef(signals.now);
  nowRef.current = signals.now;

  useEffect(() => {
    const prev = prevSnapshot.current;
    prevSnapshot.current = snapshot;
    const produced = deriveIncidents(prev, snapshot);
    if (produced.length === 0) return;
    const at = nowRef.current ?? Date.now();
    setNotices((current) => {
      const next = current.slice();
      for (const descriptor of produced) {
        const id = `${descriptor.kind}:${at}`;
        // Replace any still-showing notice of the SAME kind (no duplicate stack);
        // otherwise append. The id carries the raise time so a genuinely new
        // occurrence after the old one cleared gets a fresh id + timer.
        const existing = next.findIndex(
          (n) => n.descriptor.kind === descriptor.kind,
        );
        const entry: ActiveIncident = { id, descriptor, raisedAt: at };
        if (existing >= 0) next[existing] = entry;
        else next.push(entry);
      }
      return next;
    });
  }, [snapshot]);

  // Auto-dismiss the calm (`recovered`) notices once their TTL elapses. One timer
  // for the soonest-expiring calm notice; recomputed whenever the stack changes.
  // `needs_input` notices have no expiry and are never swept here.
  //
  // The TTL is measured in REAL elapsed time from when the notice was raised: the
  // timer's `delay` is `(raisedAt + TTL) − raisedAt = TTL` minus however long it
  // has already been showing, so when it fires the soonest notice HAS expired by
  // construction. We dismiss every calm notice that shares (or precedes) that
  // soonest expiry, then the effect re-runs for the next one — so a stack raised
  // at staggered times clears in order. We deliberately do NOT re-derive expiry
  // from a wall-clock read here: under fake timers the injected `now` is frozen,
  // and the timer firing is the authoritative "the TTL has passed" signal.
  useEffect(() => {
    const calm = notices.filter((n) => n.descriptor.severity === "recovered");
    if (calm.length === 0) return;
    const soonestExpiry = Math.min(
      ...calm.map((n) => n.raisedAt + RECOVERED_TTL_MS),
    );
    const raisedFloor = nowRef.current ?? Date.now();
    const delay = Math.max(0, soonestExpiry - raisedFloor);
    const timer = setTimeout(() => {
      // Drop the calm notices whose expiry is at/below the one this timer was
      // scheduled for (they've elapsed). `needs_input` notices are untouched.
      setNotices((current) =>
        current.filter(
          (n) =>
            n.descriptor.severity !== "recovered" ||
            n.raisedAt + RECOVERED_TTL_MS > soonestExpiry,
        ),
      );
    }, delay);
    return () => clearTimeout(timer);
  }, [notices]);

  const dismiss = useMemo(
    () => (id: string) =>
      setNotices((current) => current.filter((n) => n.id !== id)),
    [],
  );

  // Render oldest-first so the stack order is stable as notices come and go.
  const ordered = useMemo(
    () => [...notices].sort((a, b) => a.raisedAt - b.raisedAt),
    [notices],
  );

  return { notices: ordered, dismiss };
}
